<template>
  <accordion-item-fi
    v-if="hasTextList || approvals || icons"
    class="accordion-application-authorisation-fi"
    header-tag="h3"
    ignore-others
    scroll-to
  >
    <template #header>
      {{ $dict.get('product-Catalog.Accordions.Application-And-Authorisation') }}
    </template>
    <template #content>
      <div class="accordion-application-authorisation-fi__content">
        <div
          v-if="textListValue(textList, 'KAT_ANWENDUNG_GEN')"
          class="accordion-application-authorisation-fi__column accordion-application-authorisation-fi--application"
        >
          <richtext-fi
            class="accordion-application-authorisation-fi__text"
            :html-content="{ value: textListValue(textList, 'KAT_ANWENDUNG_GEN') }"
          />
        </div>
        <div
          v-if="textListValue(textList, 'KAT_BAUSTOFF_GEN')"
          class="accordion-application-authorisation-fi__column accordion-application-authorisation-fi--material"
        >
          <richtext-fi
            class="accordion-application-authorisation-fi__text"
            :html-content="{ value: textListValue(textList, 'KAT_BAUSTOFF_GEN') }"
          />
          <!-- eslint-disable vue/no-v-html -->
          <span
            class="accordion-application-authorisation-fi__material-disclaimer"
            v-html="materialDisclaimer"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>
        <div
          v-if="approvals"
          class="accordion-application-authorisation-fi__column accordion-application-authorisation-fi--approval"
        >
          <div class="accordion-application-authorisation-fi__text">
            <div class="h10">
              {{ $dict.get('product-Catalog.Accordions.Authorisation') }}
            </div>

            <richtext-fi>
              <ul>
                <li
                  v-for="item in approvals"
                  :key="item"
                >
                  {{ item }}
                </li>
              </ul>
            </richtext-fi>
          </div>
        </div>
        <div
          v-if="icons"
          class="accordion-application-authorisation-fi__column accordion-application-authorisation-fi--icons"
        >
          <div
            v-for="icon in icons"
            :key="icon.Url"
            class="accordion-application-authorisation-fi__icon"
          >
            <cloud-image-fi
              :src="icon.Url"
              :alt="icon.Label"
              height="45"
              params="func=bound"
              do-not-replace-url
            />
          </div>
        </div>
      </div>
    </template>
  </accordion-item-fi>
</template>

<script setup lang="ts">
import { AccordionItemFi, CloudImageFi, RichtextFi } from 'atoms';
import { computed } from 'vue';

import { useBreakpoints } from '@/composables/breakpoint';
import { useCatalogTextList } from '@/composables/catalogTextList';
import { useDict, useFischer } from '@/composables/fischerPlugin';

defineOptions({ name: 'AccordionApplicationAuthorisationFi' });

interface TextListItem {
  Key?: string;
  AttributeKey?: string;
  Value?: string;
}

interface MediaItem {
  OutputType?: string;
  Type?: string;
  Url?: string;
  Label?: string;
}

interface Approval {
  nr?: string;
}

interface Props {
  catalogData?: {
    TextList?: TextListItem[];
    Documents?: {
      Approvals?: Approval[];
    };
    Media?: MediaItem[];
  };
}

const props = withDefaults(defineProps<Props>(), {
  catalogData: () => ({}),
});

useBreakpoints();

const { textListValue } = useCatalogTextList();
const fischer = useFischer();
const dict = useDict();

const textList = computed(() => props.catalogData?.TextList);

const hasTextList = computed(
  () =>
    textListValue(textList.value, 'KAT_ANWENDUNG_GEN') ||
    textListValue(textList.value, 'KAT_BAUSTOFF_GEN'),
);

const materialDisclaimer = computed(() =>
  dict
    .get('product-Catalog.Block-Labels.Application-and-Permission.Materials-Disclaimer', [
      fischer.pages.downloadCenter().url,
    ])
    .replace('{url}', fischer.pages.downloadCenter().url),
);

const approvals = computed(() => {
  const result: string[] = [];

  if (props.catalogData?.Documents?.Approvals) {
    props.catalogData.Documents.Approvals.forEach((approval) => {
      if (approval.nr) {
        result.push(approval.nr);
      }
    });

    if (result.length) {
      return result;
    }
  }

  return null;
});

const icons = computed(() => {
  const media = props.catalogData?.Media;
  if (!media) {
    return null;
  }

  const filtered = media.filter(
    (o) => o.OutputType === 'PR' && (o.Type === 'Icon' || o.Type === 'Logo'),
  );

  if (!filtered.length) {
    return null;
  }

  return filtered;
});
</script>

<style src="./accordion-application-authorisation-fi" />
